import { useState, useEffect, useCallback } from 'react'
import isEqual from 'lodash/isEqual'
import { useInterval } from 'hooks/UseInterval'
import { useOnScroll } from 'hooks/UseOnScroll'

// Level represents the heading level of the HTML tag
export type Level = 0 | 1 | 2 | 3
// Depth represents the depth of the heading in the hierarchy
export type Depth = 0 | 1 | 2 | 3
export type HeadingTag = 'H1' | 'H2' | 'H3' | 'H4'

export interface Heading {
  blockId: string
  element: HTMLElement
  title: string | null
  level: Level
  depth: Depth
}

/**
 * Vertical offset above which a heading is considered the current heading
 */
const SPY_OFFSET = 200

const getTextFromElement = (el: Element): string => {
  let text = ''

  el.childNodes.forEach((child) => {
    if (child.nodeName.toLowerCase() === 'mark') {
      // for comments the last child always holds the text / comments are never nested
      text += child.lastChild?.textContent
    } else if ((child as Element).innerHTML === (child as HTMLElement).innerText) {
      // if child is a node which only contains text terminate the recursion
      text += child.textContent
    } else if ((child as HTMLElement).dataset?.presenceCursor === 'true') {
      // ignore presence cursor content (user name)
      text += ''
    } else {
      // process the child as complex element as well (can have other marks or inline HTML)
      text += getTextFromElement(child as Element)
    }
  })

  return text
}

const elementToHeading = (element: HTMLHeadingElement): Heading => {
  const levels: { [key in HeadingTag]: Level } = {
    H1: 0,
    H2: 1,
    H3: 2,
    H4: 3,
  }

  const level = levels[element.tagName as HeadingTag]

  return {
    blockId: (element.getAttribute('blockid') as string) || (element.getAttribute('data-blockid') as string),
    element: element,
    title: getTextFromElement(element),
    level: level,
    depth: level,
  }
}

/**
 * Dedent the depths of the headings that are not titles.
 * Example:
 *
 * Level 0 -> Depth 0
 * Level 2 -> Depth 1
 * Level 3 -> Depth 2
 *
 * @param {Heading[]} headings List of raw headings
 * @return {Heading[]} Copy of the headings list with compacted depths
 */
export const dedent = (headings: Heading[] = []): Heading[] => {
  const nonTitleHeadings = headings.filter((heading) => heading.level > 0)
  const lowestDepth = Math.min(...nonTitleHeadings.map((heading) => heading.depth as number))

  if (lowestDepth === 1) {
    return headings
  }

  return headings.map((heading) => {
    if (heading.level === 0) {
      return heading
    }
    // Max dedent level should always be 1 because 0 is reserved for Titles
    const newDepth = (heading.depth - lowestDepth + 1) as Depth
    return { ...heading, depth: newDepth }
  })
}

export const calculateHeadings = () => {
  const headingElements = document.querySelectorAll(
    `#main-content h1[blockid], #main-content h2[blockid], #main-content h3[blockid], #main-content h4[blockid],
    #main-content h1[data-blockid], #main-content h2[data-blockid], #main-content h3[data-blockid], #main-content h4[data-blockid]`,
  )
  let headings: Heading[] = []

  for (const headingElement of headingElements) {
    headings.push(elementToHeading(headingElement as HTMLHeadingElement))
  }

  return dedent(headings)
}

export const useTableOfContents = (): {
  headings: Heading[]
  currentBlockId?: string
} => {
  const [currentBlockId, setCurrentBlockId] = useState<string>()
  const [headings, setHeadings] = useState<Heading[]>([])

  const detectCurrentHeading = useCallback(() => {
    let currentCandidate
    for (const heading of headings) {
      const headingElement =
        document.querySelector(`[blockid='${heading.blockId}']`) ||
        document.querySelector(`[data-blockid='${heading.blockId}']`)

      if (headingElement && headingElement.getBoundingClientRect().top < SPY_OFFSET) {
        currentCandidate = heading
      }
    }
    if (currentCandidate) {
      setCurrentBlockId(currentCandidate.blockId)
    }
  }, [headings])

  useOnScroll(detectCurrentHeading)

  useEffect(() => {
    setHeadings(calculateHeadings())
  }, [])

  useInterval(() => {
    setHeadings((prevHeadings) => {
      const currentHeadings = calculateHeadings()
      if (isEqual(prevHeadings, currentHeadings)) {
        return prevHeadings
      }
      return currentHeadings
    })
    if (!currentBlockId) {
      detectCurrentHeading()
    }
  }, 500)

  return {
    currentBlockId: currentBlockId,
    headings: headings,
  }
}
